import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  initializePaddle,
  type Paddle,
  type Environments,
} from '@paddle/paddle-js';

import { apiRequest } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';

export interface BillingConfig {
  environment: Environments;
  client_token: string;
  prices: { pro_monthly: string; pro_yearly: string };
  configured: boolean;
}

export type BillingInterval = 'monthly' | 'yearly';

/**
 * Loads Paddle checkout config, lazily initializes Paddle.js, and opens the
 * overlay checkout for a Pro upgrade.
 *
 * On `checkout.completed` it invalidates the auth/usage/subscription queries.
 * The webhook flips plan_tier server-side a beat later, so callers should poll
 * `me.plan_tier` (via the onCompleted callback) to reflect activation.
 *
 * Backend is the single source of truth: /api/billing/config supplies the
 * environment + token + price ids, so sandbox->production and price changes
 * need no frontend redeploy. `configured` is false until Paddle is wired —
 * callers keep the upgrade CTA hidden/inert until then.
 */
export function useUpgradeCheckout(opts?: { onCompleted?: () => void }) {
  const { me, viewAsClientId } = useAuth();
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ['billing', 'config'],
    queryFn: () => apiRequest<BillingConfig>('/billing/config'),
    staleTime: Infinity,
  });

  const paddleRef = useRef<Paddle | null>(null);
  const initStartedRef = useRef(false);
  const [ready, setReady] = useState(false);

  // onCompleted can change between renders; read it from a ref inside the
  // (once-registered) eventCallback to avoid a stale closure.
  const onCompletedRef = useRef(opts?.onCompleted);
  useEffect(() => {
    onCompletedRef.current = opts?.onCompleted;
  });

  useEffect(() => {
    const cfg = configQuery.data;
    if (!cfg?.configured || initStartedRef.current) return;
    // Set synchronously so React StrictMode's double-invoke can't double-init
    // (Paddle.Initialize throws if called twice on a page).
    initStartedRef.current = true;
    initializePaddle({
      token: cfg.client_token,
      environment: cfg.environment,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed') {
          queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
          queryClient.invalidateQueries({ queryKey: ['usage'] });
          queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
          onCompletedRef.current?.();
        }
      },
    }).then((paddle) => {
      if (paddle) {
        paddleRef.current = paddle;
        setReady(true);
      }
    });
  }, [configQuery.data, queryClient]);

  const clientId = viewAsClientId ?? me?.memberships?.[0]?.client_id;
  const prices = configQuery.data?.prices;

  function openCheckout(interval: BillingInterval) {
    const paddle = paddleRef.current;
    if (!paddle || !prices || !clientId || !me?.email) return;
    const priceId = interval === 'yearly' ? prices.pro_yearly : prices.pro_monthly;
    if (!priceId) return;
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: { email: me.email },
      customData: { client_id: clientId },
    });
  }

  return {
    configured: !!configQuery.data?.configured,
    ready,
    openCheckout,
  };
}
